import { CreateExpensesExpensableCostCentersInput } from 'generated/resources/graphql'

import { AllocationReference, AssignmentsChange } from 'modules/cost_centers/lib/types'

export interface LocationData {
  country: string
  administrativeAreaLevel1?: string
  administrativeAreaLevel2?: string
  locality?: string
}

export interface Destination {
  id: string
  locationName: string
  locationData: LocationData
  arrivalDatetime: Date | null
}

export interface DayExclusion {
  date: Date
  breakfastProvided: boolean
  lunchProvided: boolean
  dinnerProvided: boolean
  accommodationProvided: boolean
}

export type CostCenterAllocationsChange =
  | { assignments: AssignmentsChange }
  | { allocation: AllocationReference }

export interface PerDiemWizardData {
  // Step 1: Trip Details
  startDate: Date | null
  endDate: Date | null
  destinations: Destination[]

  // Step 2: Company-paid items
  dayExclusions: DayExclusion[]

  // Budget and Project
  budgetId?: string
  // projectId is actually projectWorkerId (the employee-project assignment ID)
  projectId?: string
  subprojectId?: string

  // Cost Centers
  costCenters?: CostCenterFormValue

  // Additional Information
  description?: string
  internalReference?: string
  files: File[]

  // Employee
  employeeId?: string

  // Requester
  requesterId?: string

  tripName?: string
}

/**
 * Accepts either the strict CostCenterAllocationsChange union (from the wizard context)
 * or the looser form-value shape (where both `allocation` and `assignments` are optional)
 * and maps it to the GraphQL mutation input type.
 */
export type CostCenterFormValue =
  | CostCenterAllocationsChange
  | {
      allocation?: AllocationReference
      assignments?: AssignmentsChange
    }

/**
 * Maps cost center data to the shape expected by GraphQL mutation inputs,
 * narrowing `costCenterId` from `number | null | undefined` to `number`.
 */
export const mapCostCentersForMutation = (
  value: CostCenterFormValue | undefined | null
): CreateExpensesExpensableCostCentersInput | undefined => {
  if (!value) return undefined

  if ('allocation' in value && value.allocation) {
    return { allocation: value.allocation }
  }

  if ('assignments' in value && value.assignments) {
    return {
      assignments: {
        assignmentsToCreate: value.assignments.assignmentsToCreate
          .filter((a): a is typeof a & { costCenterId: number } => a.costCenterId != null)
          .map((a) => ({
            costCenterId: a.costCenterId,
            percentage: a.percentage,
            startDate: a.startDate,
            endDate: a.endDate,
          })),
        assignmentsToUpdate: [],
      },
    }
  }

  return undefined
}

export type ScopedEditSection =
  | 'tripInformation'
  | 'companyPaid'
  | 'basicInformation'
  | 'budgetAndProject'
  | 'costCenters'
  | 'additionalInformation'

export const createEmptyDestination = (): Destination => ({
  id: crypto.randomUUID(),
  locationName: '',
  locationData: {
    country: '',
  },
  arrivalDatetime: null,
})

export const createInitialWizardData = (): PerDiemWizardData => ({
  startDate: null,
  endDate: null,
  destinations: [createEmptyDestination()],
  dayExclusions: [],
  costCenters: undefined,
  files: [],
})
